<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="h-screen w-screen overflow-y-auto bg-base-100" @scroll="handleScroll">
      <WaveAnimation :visible="showWave" />
      <div class="section" id="slide1">
        <div id="slide1-container">
          <!-- Replace static image with BackgroundVideo component -->
          <BackgroundVideo
            desktopVideo="/videos/AboutLandingVideo.mp4"
            mobileVideo="/videos/AboutLandingVideoMobile.mp4"
            placeholderImage="/videos/AboutLandingVideo.jpg"
            altText="About STE"
            videoId="aboutVideo"
            @video-loaded="handleVideoLoad"
            @video-error="handleVideoError"
          />
          <div class="slideText">
            <h1 class="slideTitle"></h1>
            <div class="sub-text-with-image">
              <div class="text-content">
                <h3 class="slideSub"></h3>
                <h4 class="slideSubSub"></h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section" id="slide2">
        <div class="section-content">
          <OurStory />
          <!-- Add content for slide2 if needed -->
        </div>
      </div>
      <div class="section" id="slide3">
        <div class="section-content">
          <MeetTheTeam />
        </div>
      </div>
      <div class="section" id="slide4">
        <div class="section-content">
          <OurValues />
        </div>
      </div>
      <div class="section min-h-0" id="slide5">
        <div class="section-content">
          <HendricksHoldings />
        </div>
      </div>
      <div class="section min-h-0" id="slide6">
        <div class="section-content">
          <ThisIsBeloit />
        </div>
      </div>
      <div class="section min-h-0" id="slide7">
        <div class="section-content">
          <ContactSection />
        </div>
      </div>
    </div>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue';
import OurStory from '@/components/OurStory.vue';
import MeetTheTeam from '@/components/MeetTheTeam.vue';
import OurValues from '@/components/OurValues.vue';
import HendricksHoldings from '@/components/HendricksHoldings.vue';
import ThisIsBeloit from '@/components/ThisIsBeloit.vue';
import ContactSection from '@/components/ContactSection.vue';
import BackgroundVideo from '@/components/BackgroundVideo.vue';
import WaveAnimation from '@/components/WaveAnimation.vue';
import { nextTick } from 'vue';

export default {
  name: 'AboutUs',
  components: {
    NavBar,
    OurStory,
    MeetTheTeam,
    OurValues,
    HendricksHoldings,
    ThisIsBeloit,
    ContactSection,
    BackgroundVideo,
    WaveAnimation
  },
  data() {
    return {
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      videoLoading: true,
      videoReady: false,
      showWave: false,
      sections: [
        { id: 'slide2', name: 'HISTORY', bgClass: 'bg-gray-200' },
        { id: 'slide3', name: 'LEADERSHIP', bgClass: 'bg-gray-200' },
        { id: 'slide4', name: 'VALUES', bgClass: 'bg-gray-300' },
        { id: 'slide5', name: 'HENDRICKS', bgClass: 'bg-gray-400' },
        { id: 'slide6', name: 'BELOIT', bgClass: 'bg-gray-400' },
        { id: 'slide7', name: 'CONTACT', bgClass: 'bg-gray-400' },
      ],
    };
  },
  mounted() {
    this.initIntersectionObserver();
    this.handleScroll();

    this.scrollToHash();

    // Watch for route changes to handle hash scrolling
    this.$watch('$route', (to) => {
      if (to.hash) {
        this.scrollToHash();
      }
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            console.log(`Section ${entry.target.id} is now visible`);
          } else {
            entry.target.classList.remove('visible');
            console.log(`Section ${entry.target.id} is now hidden`);
          }
        });
      }, options);

      document.querySelectorAll('.section').forEach((section) => {
        this.observer.observe(section);
      });
    },
    handleScroll() {
      const container = document.querySelector('.overflow-y-auto');
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    scrollToNextSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('.section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      const nextSection = sections.find(
        (section) => section.offsetTop > currentScroll + tolerance
      );

      if (nextSection) {
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },

    scrollToPreviousSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('.section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      let previousSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop + tolerance < currentScroll) {
          previousSection = section;
          break;
        }
      }

      if (previousSection) {
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    scrollToTop() {
      const container = document.querySelector('.overflow-y-auto');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    },
    scrollToSection(id) {
      console.log(`Scrolling to section: ${id}`);
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    scrollToHash() {
      nextTick(() => {
        setTimeout(() => {
          const hash = this.$route.hash;
          if (hash) {
            // If the hash starts with #value-, first make sure we're on the values section
            if (hash.startsWith('#value-')) {
              // First scroll to the values section
              const valuesSection = document.getElementById('slide4');
              if (valuesSection) {
                valuesSection.scrollIntoView({ behavior: 'smooth' });
                
                // Then scroll to the specific value after a short delay
                setTimeout(() => {
                  const element = document.querySelector(hash);
                  if (element) {
                    // Scroll the element into view with an offset to account for fixed header
                    const headerOffset = 80; // Adjust based on your header height
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                    console.log(`Scrolled to value: ${hash}`);
                  }
                }, 500); // Short delay to allow first scroll to complete
              }
            } else {
              // Regular hash navigation
              const element = document.querySelector(hash);
              if (element) {
                console.log(`Scrolling to element: ${hash}`);
                element.scrollIntoView({ behavior: 'smooth' });
              } else {
                console.log(`Element not found for hash: ${hash}`);
              }
            }
          }
        }, 500); // Adjust delay as necessary
      });
    },
    handleVideoLoad() {
      this.videoLoading = false;
      this.videoReady = true;
    },
    handleVideoError() {
      console.error('Error loading about page video');
      this.videoLoading = false;
    },
  },
};
</script>

<style scoped lang="scss">

.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  z-index: 100;
}

.h-screen.overflow-y-auto {
  background: radial-gradient(ellipse at bottom, #363741 0%, #1d1e26 90%);
  background-attachment: fixed;
  position: relative;
  scrollbar-width: none;
  z-index: 0;
  -webkit-overflow-scrolling: touch;
}

#slide1 {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

#slide6 {
  height: auto;
  min-height:auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

#slide1-container {
  z-index: -25;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: left;
  padding: 0% 5%;
  height: 100vh; /* Explicit height */
  width: 100%;
  position: relative; /* Add this line */
  overflow: hidden; /* Add this to contain absolutely positioned children */
}

#bgVideo {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
}

#bgImage {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
}

.section {
  height: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  z-index: 1; /* Ensure sections are above the wave */
}

.section-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.slideText {
  max-width: 70%;
  margin-top: 65vh;
  margin-left: 2vw;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: rgb(183, 185, 186);
  font-family: "PT Sans", sans-serif;
}

.sub-text-with-image {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.text-content {
  display: flex;
  flex-direction: column;
}

.slideTitle {
  color: rgb(183, 185, 186);
  font-family: "PT Sans", sans-serif;
  line-height: 1.25;
}

.slideSub {
  font-family: "PT Sans", sans-serif;
  line-height: 1.25;
  padding-bottom:2rem;
  color: rgb(183, 185, 186);
}

.slideSubSub {
  font-family: "PT Sans", sans-serif;
  line-height: 1;
  outline: 2px inset rgb(255, 255, 255);
  padding:1rem;
  font-weight:bold;
  max-width:40%;
  margin-left:1rem;
  text-align:center;
}

.scroll-arrow {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2.5rem;
  color: #b7b9ba;
  cursor: pointer;
  animation: bob 2s infinite;
  transition: transform 0.3s, opacity 0.3s;
  z-index: 100;
}

.up-arrow {
  bottom: 50px;

  @media (max-width: 768px) {
    visibility: hidden;
  }
}

.down-arrow {
  bottom: 10px;
}

.scroll-arrow:hover {
  transform: translateX(-50%) scale(1.2);
}

@keyframes bob {
  0%, 100% {
    transform: translate(-50%, 0);
  }
  50% {
    transform: translate(-50%, 10px);
  }
}
</style>